import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../../stores/global-settings-branches';
import globalSettingsStores from '../../../../../stores/settings-stores';
import { getHebrewYear } from '../../../../../utils/hebrew-date-utils';
import { FilterExpanderWithListOfCheckboxes } from '../../../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { CustomExpander } from '../../../../../components/CustomExpander/CustomExpander';
import { RadioButton } from 'primereact/radiobutton';
import { Button } from 'primereact/button';
import { CourseFinancialFilterDatamodel } from './course-financial-side-bar-filter-datamodel';
import { CourseStatus } from '../../../../../api/types/programs/course/course-status';

interface YearOption {
  year: string;
  code: string;
  value: number;
}

interface CourseFinancialSideBarFilterProps {
  onSearch: (filters: CourseFinancialFilterDatamodel) => void;
}

export const CourseFinancialSideBarFilter = observer(
  ({ onSearch }: CourseFinancialSideBarFilterProps) => {
    const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
    const [selectedProgram, setSelectedProgram] = useState<number | null>(null);
    const [selectedYears, setSelectedYears] = useState<number[]>([]);
    const [selectedCourse, setSelectedCourse] = useState<number | null>(null);
    const [courseStatus, setCourseStatus] = useState<CourseStatus | null>(null);
    const [availableCourses, setAvailableCourses] = useState<any[]>([]);

    // Generate years array
    const years: YearOption[] = [];
    for (let year = 1980; year <= 2030; year++) {
      years.push({
        year: `${getHebrewYear(year)} (${year} - ${year + 1})`,
        code: 'year',
        value: year,
      });
    }

    const courseStatuses = Object.values(CourseStatus).filter(
      (value) => typeof value === 'string',
    ) as CourseStatus[];

    useEffect(() => {
      // Update available courses when program is selected
      if (selectedProgram) {
        const program = globalSettingsStores.abstractProgram.find(
          (p) => p.id === selectedProgram,
        );
        setAvailableCourses(program?.courses || []);
      } else {
        setAvailableCourses([]);
      }
      // Reset selected course when program changes
      setSelectedCourse(null);
    }, [selectedProgram]);

    const handleSearch = () => {
      const filters: CourseFinancialFilterDatamodel = {
        branchIds: selectedBranches,
        programId: selectedProgram,
        startYears: selectedYears,
        courseId: selectedCourse,
        courseStatus: courseStatus,
      };
      onSearch(filters);
    };

    const handleClearAllFilters = () => {
      setSelectedBranches([]);
      setSelectedProgram(null);
      setSelectedYears([]);
      setSelectedCourse(null);
      setCourseStatus(null);
      setAvailableCourses([]);
    };

    const styles = {
      container: {
        height: '100%',
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      header: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#333',
      },
      headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        gap: '8px',
      },
      content: {
        flex: 1,
        overflowY: 'auto' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        paddingRight: '4px',
      },
      radioGroup: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '8px',
        padding: '8px',
      },
      radioOption: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      },
      buttonContainer: {
        marginTop: '16px',
        padding: '8px',
        borderTop: '1px solid #e9ecef',
      },
      searchButton: {
        width: '100%',
        backgroundColor: '#2196F3',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '1rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.headerRow}>
          <div style={styles.header}>סינונים</div>
          <Button
            icon="pi pi-filter-slash"
            text
            severity="secondary"
            tooltip={'נקה הכל'}
            onClick={handleClearAllFilters}
          />
        </div>
        <div style={styles.content}>
          <FilterExpanderWithListOfCheckboxes
            title="שלוחות"
            items={globalSettingsBranches.branches}
            selectedItems={selectedBranches}
            onItemToggle={(branchId) => {
              setSelectedBranches((prev) =>
                prev.includes(branchId)
                  ? prev.filter((b) => b !== branchId)
                  : [...prev, branchId],
              );
            }}
            getItemLabel={(branch) => branch.branchName}
            getItemKey={(branch) => branch.id}
            defaultExpanded={true}
            searchPlaceholder="חפש שלוחה..."
          />

          <CustomExpander>
            <div>תכנית לימודים</div>
            <div style={styles.radioGroup}>
              {globalSettingsStores.abstractProgram.map((program) => (
                <div key={program.id} style={styles.radioOption}>
                  <RadioButton
                    inputId={program.id.toString()}
                    name="program"
                    value={program.id}
                    onChange={(e) => setSelectedProgram(e.value)}
                    checked={selectedProgram === program.id}
                  />
                  <label htmlFor={program.id.toString()}>
                    {program.programName}
                  </label>
                </div>
              ))}
            </div>
          </CustomExpander>

          <FilterExpanderWithListOfCheckboxes
            title="שנת התחלה"
            items={years}
            selectedItems={selectedYears.map(String)}
            onItemToggle={(yearStr) => {
              const year = parseInt(yearStr);
              setSelectedYears((prev) =>
                prev.includes(year)
                  ? prev.filter((y) => y !== year)
                  : [...prev, year],
              );
            }}
            getItemLabel={(yearOption) => yearOption.year}
            getItemKey={(yearOption) => yearOption.value.toString()}
            searchPlaceholder="חפש שנה..."
          />

          {selectedProgram && availableCourses.length > 0 && (
            <CustomExpander>
              <div>שם קורס</div>
              <div style={styles.radioGroup}>
                {availableCourses.map((course) => (
                  <div key={course.id} style={styles.radioOption}>
                    <RadioButton
                      inputId={course.id.toString()}
                      name="course"
                      value={course.id}
                      onChange={(e) => setSelectedCourse(e.value)}
                      checked={selectedCourse === course.id}
                    />
                    <label htmlFor={course.id.toString()}>
                      {course.courseName}
                    </label>
                  </div>
                ))}
              </div>
            </CustomExpander>
          )}

          <CustomExpander>
            <div>סטטוס קורס</div>
            <div style={styles.radioGroup}>
              {courseStatuses.map((status) => (
                <div key={status} style={styles.radioOption}>
                  <RadioButton
                    inputId={status}
                    name="status"
                    value={status}
                    onChange={(e) => setCourseStatus(e.value)}
                    checked={courseStatus === status}
                  />
                  <label htmlFor={status}>
                    {CourseStatus.getDisplayText(status)}
                  </label>
                </div>
              ))}
            </div>
          </CustomExpander>
        </div>
        <div style={styles.buttonContainer}>
          <Button
            label="חפש"
            icon="pi pi-search"
            style={styles.searchButton}
            onClick={handleSearch}
          />
        </div>
      </div>
    );
  },
);
